import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { SHARED_MAP_CONTROLS, initialMapType, writeStoredMapType } from '../../lib/mapControls.js';
import './LocationPicker.less';

/**
 * Map + geolocation picker for the activation Configure step.
 *
 * Flow:
 *   1. On mount, request browser geolocation (one-shot).
 *   2. On grant — center on user, place draggable marker, emit lat/lng +
 *      reverse-geocoded locationText.
 *   3. On deny / unsupported — center on fallback (env VITE_MAP_FALLBACK_*
 *      or a sensible default); installer clicks the map to drop the pin.
 *   4. Click or drag — update lat/lng and reverse-geocode an editable
 *      locationText suggestion.
 *
 * Parent owns the values (lat/lng/locationText). This component never
 * mutates parent state directly; it just calls onChange with whatever
 * fields it has new values for. The parent is responsible for "manual
 * override protection" (e.g. not overwriting an installer's typed
 * locationText with reverse-geocoding output).
 *
 * Failure modes are handled inline — if the Google Maps key is missing
 * or the API fails to load, the component renders a compact notice and
 * the parent's plain lat/lng + locationText inputs (which live below
 * this component) continue to work.
 */

// Fallback when geolocation is denied / unavailable. Defaults to the
// geographic center of Barbados (≈ Bridgetown's hinterland). Override
// per-deployment with VITE_MAP_FALLBACK_LAT / VITE_MAP_FALLBACK_LNG
// (and VITE_MAP_FALLBACK_ZOOM if a different island/country needs a
// wider or tighter view).
const FALLBACK_CENTER = {
  lat: Number(import.meta.env.VITE_MAP_FALLBACK_LAT) || 13.1939,
  lng: Number(import.meta.env.VITE_MAP_FALLBACK_LNG) || -59.5432,
};
const DEFAULT_ZOOM = 16;
// Barbados fits comfortably at zoom 11 (whole island visible with road labels).
const FALLBACK_ZOOM = Number(import.meta.env.VITE_MAP_FALLBACK_ZOOM) || 11;

const GEOCODE_DEBOUNCE_MS = 500;

// Helper: format a Google Maps geocoder result into a short address string.
function formatAddress(result) {
  if (!result) return '';
  return result.formatted_address || '';
}

// Strict coordinate parser. JS's `Number('')` is 0 and `Number.isFinite(0)`
// is true — that quirk would convince this component "we already have
// coords from props" before geolocation ever fires. We require the raw
// value to be present (non-empty string, non-null) before coercing.
function parseCoord(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const LocationPicker = ({ value, onChange }) => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'gc-installer-maps',
    googleMapsApiKey: apiKey,
  });

  // Marker position is the source of truth for the map view; the parent
  // value props are mirrored in if they change externally (e.g. installer
  // types lat/lng manually below the map). parseCoord rejects '' / null
  // so an unset parent value doesn't pre-empt geolocation.
  const [marker, setMarker] = useState(() => {
    const lat = parseCoord(value?.lat);
    const lng = parseCoord(value?.lng);
    return lat != null && lng != null ? { lat, lng } : null;
  });
  const [geoState, setGeoState] = useState('idle'); // idle | requesting | granted | denied
  const mapRef = useRef(null);
  const geocodeTimer = useRef(null);
  const geocoderRef = useRef(null);

  // Mirror external lat/lng edits into the marker (one-way).
  useEffect(() => {
    const lat = parseCoord(value?.lat);
    const lng = parseCoord(value?.lng);
    if (lat != null && lng != null) {
      const next = { lat, lng };
      // Avoid a state update if values didn't actually change.
      setMarker((prev) =>
        prev && prev.lat === next.lat && prev.lng === next.lng ? prev : next,
      );
    }
  }, [value?.lat, value?.lng]);

  // Geolocation on mount. We never block the picker on this; if the user
  // declines or it errors we just leave the map centered on the fallback.
  // Initialize the geocoder eagerly here too — useJsApiLoader.isLoaded
  // fires as soon as the JS bundle is ready, which can be before the
  // <GoogleMap> child mounts and triggers onLoadMap. Without this, the
  // first reverse-geocode call (right after geolocation succeeds) would
  // silently bail because geocoderRef.current was still null.
  useEffect(() => {
    if (!isLoaded) return;
    if (!geocoderRef.current && window.google?.maps?.Geocoder) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    if (marker) return; // already have coords from props
    if (!('geolocation' in navigator)) {
      setGeoState('denied');
      return;
    }
    setGeoState('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // High accuracy when available; the installer is standing next to
        // the charger and we want the pin on the unit, not the rooftop.
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMarker(next);
        setGeoState('granted');
        onChange({ lat: next.lat, lng: next.lng });
        scheduleReverseGeocode(next);
      },
      () => { setGeoState('denied'); },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  // Re-centre the map when the marker changes from elsewhere.
  useEffect(() => {
    if (mapRef.current && marker) {
      mapRef.current.panTo(marker);
    }
  }, [marker]);

  const onLoadMap = useCallback((mapInstance) => {
    mapRef.current = mapInstance;
    if (window.google && window.google.maps && !geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, []);

  const scheduleReverseGeocode = useCallback((pos) => {
    if (!geocoderRef.current) return;
    if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    geocodeTimer.current = setTimeout(() => {
      geocoderRef.current.geocode({ location: pos }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          onChange({ locationText: formatAddress(results[0]) });
        }
      });
    }, GEOCODE_DEBOUNCE_MS);
  }, [onChange]);

  const handlePositionChange = useCallback((pos) => {
    setMarker(pos);
    onChange({ lat: pos.lat, lng: pos.lng });
    scheduleReverseGeocode(pos);
  }, [onChange, scheduleReverseGeocode]);

  const handleMapClick = useCallback((e) => {
    if (!e || !e.latLng) return;
    handlePositionChange({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [handlePositionChange]);

  const handleMarkerDragEnd = useCallback((e) => {
    if (!e || !e.latLng) return;
    handlePositionChange({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [handlePositionChange]);

  const mapCenter = useMemo(() => marker || FALLBACK_CENTER, [marker]);
  const mapZoom = marker ? DEFAULT_ZOOM : FALLBACK_ZOOM;

  if (!apiKey) {
    return (
      <div className="location-picker-notice">
        Map disabled — set <code>VITE_GOOGLE_MAPS_API_KEY</code> to enable
        the visual location picker. Use the latitude / longitude fields
        below to enter coordinates manually.
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="location-picker-notice location-picker-error">
        Map failed to load. Use the latitude / longitude fields below
        to enter coordinates manually.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="location-picker-loading">
        <span className="activation-spinner small" /> Loading map…
      </div>
    );
  }

  return (
    <div className="location-picker">
      <div className="location-picker-hint">
        {geoState === 'requesting' && 'Locating you…'}
        {geoState === 'granted'    && 'Pin set to your current location — drag to adjust.'}
        {geoState === 'denied'     && 'Location permission denied — click the map to drop the pin.'}
        {geoState === 'idle'       && marker && 'Drag the pin to adjust, or click anywhere on the map.'}
        {geoState === 'idle'       && !marker && 'Click the map to drop the pin.'}
      </div>
      <div className="location-picker-map">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onLoadMap}
          onClick={handleMapClick}
          mapTypeId={initialMapType('roadmap')}
          onMapTypeIdChanged={() => {
            const m = mapRef.current;
            if (m && typeof m.getMapTypeId === 'function') writeStoredMapType(m.getMapTypeId());
          }}
          options={{
            streetViewControl: false,
            ...SHARED_MAP_CONTROLS,
            clickableIcons: false,
          }}
        >
          {marker && (
            <MarkerF
              position={marker}
              draggable
              onDragEnd={handleMarkerDragEnd}
            />
          )}
        </GoogleMap>
      </div>
    </div>
  );
};

export default LocationPicker;
